import React, { useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography
} from '@mui/material';
import { useSelector, useDispatch } from "react-redux";
import AlertDialog from '../components/AlertDialog';
import { api } from 'common';
import { useTranslation } from "react-i18next";
import { FONT_FAMILY, formatCurrency } from "../common/sharedFunctions";
import AdminStatusBadge from "../components/AdminStatus";
import { EmptyState, PageHeader, SectionHeader, StatusBadge, SurfaceCard } from "../components/adminPrimitives";
import adminTokens from "../styles/adminDesignTokens";

const FALLBACK_LABELS = {
    add_to_wallet_title: "Wallet Management",
    wallet_management_title: "Wallet Management",
    wallet_management_help: "Check wallet balances and approve add or deduction adjustments for customers, drivers, and fleet admins / logistics companies.",
    wallet_user_name: "Driver / Customer / Fleet Admin / Logistics Company",
    user_type: "User Type",
    amount: "Amount",
    current_wallet_balance: "Current Wallet Balance",
    add_wallet_amount: "Add Wallet Amount",
    deduct_wallet_amount: "Deduct Wallet Amount",
    approve_add_wallet: "Approve Add",
    approve_deduct_wallet: "Approve Deduction",
    customer: "Customer",
    driver: "Driver",
    fleetadmin: "Fleet Admin / Logistics Company",
    no_users_found: "No users found",
    success: "Success",
    no_details_error: "Please fill up all the details properly.",
    wallet_balance_low: "Wallet balance is low."
};

const label = (t, key) => {
    const value = t(key);
    return value === key ? (FALLBACK_LABELS[key] || key) : value;
};

export default function AddMoney() {
    const { t, i18n } = useTranslation();
    const isRTL = i18n.dir();
    const {
        addToWallet,
        deductFromWallet,
        fetchUsersOnce
    } = api;
    const settings = useSelector(state => state.settingsdata.settings);
    const dispatch = useDispatch();
    const userdata = useSelector(state => state.usersdata);
    const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
    const [amounts, setAmounts] = useState({});
    const [busyKey, setBusyKey] = useState("");

    useEffect(() => {
        dispatch(fetchUsersOnce());
    }, [dispatch, fetchUsersOnce]);

    const walletUsers = useMemo(() => {
        const users = userdata.users || [];
        return users
            .filter((user) => ["customer", "driver", "fleetadmin"].includes(user.usertype))
            .sort((a, b) => {
                const aName = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
                const bName = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
                return aName.localeCompare(bName);
            });
    }, [userdata.users]);

    const userTypeCounts = useMemo(() => {
        return walletUsers.reduce((acc, user) => ({
            ...acc,
            [user.usertype]: (acc[user.usertype] || 0) + 1
        }), {});
    }, [walletUsers]);

    const setWalletAmount = (uid, field, value) => {
        setAmounts({
            ...amounts,
            [uid]: {
                ...(amounts[uid] || {}),
                [field]: value
            }
        });
    };

    const getUserName = (user) => {
        const name = `${user.firstName || ""} ${user.lastName || ""}`.trim();
        return name || user.email || user.mobile || user.id;
    };

    const handleWalletAction = async (user, action) => {
        const rawAmount = amounts[user.id] && amounts[user.id][action] ? amounts[user.id][action] : "";
        const amount = parseFloat(rawAmount);
        if (!user.id || !amount || amount <= 0) {
            setCommonAlert({ open: true, msg: label(t, "no_details_error") });
            return;
        }
        if (action === "deduct" && amount > parseFloat(user.walletBalance || 0)) {
            setCommonAlert({ open: true, msg: label(t, "wallet_balance_low") });
            return;
        }
        setBusyKey(`${user.id}_${action}`);
        try {
            if (action === "add") {
                await dispatch(addToWallet(user.id, amount));
            } else {
                await dispatch(deductFromWallet(user.id, amount));
            }
            setWalletAmount(user.id, action, "");
            dispatch(fetchUsersOnce());
            setCommonAlert({ open: true, msg: label(t, "success") });
        } catch (error) {
            setCommonAlert({ open: true, msg: error && error.message ? error.message : label(t, "no_details_error") });
        } finally {
            setBusyKey("");
        }
    };

    const handleCommonAlertClose = (e) => {
        e.preventDefault();
        setCommonAlert({ open: false, msg: '' });
    };

    return (
        <Box className="garahex-finance-wallet-management-page" sx={{ direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
            <PageHeader
                eyebrow={label(t, "finance")}
                title={label(t, "wallet_management_title")}
                description={label(t, "wallet_management_help")}
                actions={<StatusBadge tone="info">{`${walletUsers.length} ${label(t, "wallet_user_name")}`}</StatusBadge>}
            />
            <Grid container spacing={2} sx={{ mb: 2 }}>
                {[
                    { label: label(t, "customer"), value: userTypeCounts.customer || 0, color: adminTokens.colors.royalBlue },
                    { label: label(t, "driver"), value: userTypeCounts.driver || 0, color: adminTokens.colors.success },
                    { label: label(t, "fleetadmin"), value: userTypeCounts.fleetadmin || 0, color: adminTokens.colors.garahexOrange }
                ].map((item) => (
                    <Grid item xs={12} sm={4} key={item.label}>
                        <SurfaceCard sx={{ minHeight: 112 }}>
                            <Typography sx={{ color: adminTokens.colors.muted, fontSize: 13, fontWeight: 850 }}>{item.label}</Typography>
                            <Typography sx={{ color: item.color, fontSize: "1.9rem", fontWeight: 950 }}>{item.value}</Typography>
                        </SurfaceCard>
                    </Grid>
                ))}
            </Grid>
            <SurfaceCard>
                <SectionHeader
                    title={label(t, "wallet_management_title")}
                    description={label(t, "wallet_management_help")}
                />
                        <TableContainer component={Paper} sx={{ borderRadius: 4, border: `1px solid ${adminTokens.colors.border}`, boxShadow: "none", overflowX: "auto" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        {[label(t, "wallet_user_name"), label(t, "user_type"), label(t, "current_wallet_balance"), label(t, "add_wallet_amount"), label(t, "deduct_wallet_amount")].map((heading) => (
                                            <TableCell key={heading} sx={{ fontFamily: FONT_FAMILY, fontWeight: 900, color: adminTokens.colors.deepNavy, backgroundColor: "#F7FBFF", borderBottom: `1px solid ${adminTokens.colors.border}` }}>{heading}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {walletUsers.length > 0 ? walletUsers.map((user) => (
                                        <TableRow key={user.id}>
                                            <TableCell style={{ fontFamily: FONT_FAMILY }}>
                                                <Box>
                                                    <Typography variant="body2" style={{ fontFamily: FONT_FAMILY, fontWeight: "bold" }}>{getUserName(user)}</Typography>
                                                    <Typography variant="caption" style={{ fontFamily: FONT_FAMILY }}>{user.email || user.mobile || ""}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell style={{ fontFamily: FONT_FAMILY }}>
                                                <AdminStatusBadge status="ACTIVE">{label(t, user.usertype)}</AdminStatusBadge>
                                            </TableCell>
                                            <TableCell style={{ fontFamily: FONT_FAMILY, fontWeight: "bold", color: adminTokens.colors.deepNavy }}>
                                                {formatCurrency(user.walletBalance || 0, settings)}
                                            </TableCell>
                                            <TableCell style={{ minWidth: 250 }}>
                                                <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        label={label(t, "amount")}
                                                        value={(amounts[user.id] && amounts[user.id].add) || ""}
                                                        onChange={(event) => setWalletAmount(user.id, "add", event.target.value)}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        color="primary"
                                                        disabled={busyKey === `${user.id}_add`}
                                                        onClick={() => handleWalletAction(user, "add")}
                                                    >
                                                        {label(t, "approve_add_wallet")}
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                            <TableCell style={{ minWidth: 280 }}>
                                                <Box style={{ display: "flex", gap: 8, alignItems: "center" }}>
                                                    <TextField
                                                        size="small"
                                                        type="number"
                                                        label={label(t, "amount")}
                                                        value={(amounts[user.id] && amounts[user.id].deduct) || ""}
                                                        onChange={(event) => setWalletAmount(user.id, "deduct", event.target.value)}
                                                    />
                                                    <Button
                                                        variant="contained"
                                                        color="error"
                                                        disabled={busyKey === `${user.id}_deduct`}
                                                        onClick={() => handleWalletAction(user, "deduct")}
                                                    >
                                                        {label(t, "approve_deduct_wallet")}
                                                    </Button>
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    )) : (
                                        <TableRow>
                                            <TableCell colSpan={5}>
                                                <EmptyState title={label(t, "no_users_found")} />
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
            </SurfaceCard>
            <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>{commonAlert.msg}</AlertDialog>
        </Box>
    );
}
